/**
 * Onboarding Users Page
 *
 * Three sections:
 * 1. Today snapshot
 * 2. Cohort analysis heatmap
 * 3. Stalled user alerts (stuck >7 days)
 * 4. Full user list with stage badges and filtering
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { founderDashboardAPI } from '../../../services/api';
import { useEnvironment } from '../../../context/EnvironmentContext';
import DataTable from '../../../components/founder-dashboard/DataTable';
import RefreshIndicator from '../../../components/founder-dashboard/RefreshIndicator';
import TodaySnapshotCard from './components/TodaySnapshotCard';
import {
  ExclamationTriangleIcon,
  FunnelIcon,
  InformationCircleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

// Human-readable labels, badge colors, and tooltip descriptions for user stages.
// Key order here drives the cohort heatmap column order via Object.keys().
const USER_STAGE_CONFIG = {
  signed_up: { label: 'Signed Up', bg: 'bg-gray-100', text: 'text-gray-700', tip: 'Created an account. Still pending_org — no church yet.' },
  viewed_options: { label: 'Opened Wizard', bg: 'bg-blue-100', text: 'text-blue-700', tip: 'Opened the Getting Started wizard to explore options.' },
  onboarding_skipped: { label: 'Closed Wizard', bg: 'bg-yellow-100', text: 'text-yellow-800', tip: 'Closed the Getting Started wizard without taking an action.' },
  searched_church: { label: 'Searched', bg: 'bg-purple-100', text: 'text-purple-700', tip: 'Used the church search to look for their church by name.' },
  notify_me: { label: 'Notify Me', bg: 'bg-slate-100', text: 'text-slate-700', tip: 'One-tap "notify me when my church joins" — submitted email only, no city/state/description.' },
  lead_submitted: { label: 'Lead Submitted', bg: 'bg-teal-100', text: 'text-teal-700', tip: 'Church not on Flock — submitted interest form with city, state, and/or a message.' },
  join_code_attempted: { label: 'Tried Join', bg: 'bg-indigo-100', text: 'text-indigo-700', tip: 'Entered a group join code to try joining directly.' },
  join_request_sent: { label: 'Join Requested', bg: 'bg-cyan-100', text: 'text-cyan-700', tip: 'Found their church and submitted a request to join.' },
  awaiting_approval: { label: 'Awaiting Approval', bg: 'bg-amber-100', text: 'text-amber-800', tip: 'Join request pending — waiting for a church admin to approve.' },
  invited: { label: 'Invited', bg: 'bg-sky-100', text: 'text-sky-700', tip: 'Admin approved their join request and sent an invitation.' },
  personal_group_started: { label: 'Personal Group', bg: 'bg-violet-100', text: 'text-violet-700', tip: 'Started the personal group creation wizard.' },
  org_creation_started: { label: 'Creating Org', bg: 'bg-orange-100', text: 'text-orange-700', tip: 'Began creating a new organization (church leader path).' },
  activated: { label: 'Activated', bg: 'bg-emerald-100', text: 'text-emerald-700', tip: 'Joined an organization — fully onboarded and active.' },
};

function StageBadge({ stage }) {
  const config = USER_STAGE_CONFIG[stage] || {
    label: stage,
    bg: 'bg-gray-100',
    text: 'text-gray-700',
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
    >
      {config.label}
    </span>
  );
}

function formatRelativeDate(dateStr) {
  if (!dateStr) return 'Unknown';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Heatmap color based on intensity.
// In 'absolute' mode: intensity = count / maxCount (relative to the hottest cell in the table).
// In 'relative' mode: intensity = pct / 100 (so a band means "this cell is N% of its cohort").
function heatmapColor(count, maxCount, mode, pct) {
  if (!count || count === 0) return 'bg-gray-50 text-gray-400';
  const ratio = mode === 'relative'
    ? (pct || 0) / 100
    : count / Math.max(maxCount, 1);
  if (ratio >= 0.75) return 'bg-blue-600 text-white';
  if (ratio >= 0.5) return 'bg-blue-400 text-white';
  if (ratio >= 0.25) return 'bg-blue-200 text-blue-800';
  return 'bg-blue-100 text-blue-700';
}

export default function OnboardingUsersPage() {
  const { currentEnv } = useEnvironment();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [cohortData, setCohortData] = useState(null);
  const [todaySnapshot, setTodaySnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cohortLoading, setCohortLoading] = useState(true);
  const [snapshotLoading, setSnapshotLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [stageFilter, setStageFilter] = useState('all');
  const [cohortMode, setCohortMode] = useState('relative'); // 'absolute' | 'relative'

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await founderDashboardAPI.getOnboardingUsers();
      setUserData(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('[OnboardingUsers] Error fetching users:', err);
      setError(
        err.response?.data?.error || err.message || 'Failed to load users'
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchCohorts = async () => {
    setCohortLoading(true);
    try {
      const response = await founderDashboardAPI.getOnboardingUserCohorts();
      setCohortData(response.data);
    } catch (err) {
      console.error('[OnboardingUsers] Error fetching cohorts:', err);
      // Non-critical: don't set main error
    } finally {
      setCohortLoading(false);
    }
  };

  const fetchTodaySnapshot = async () => {
    setSnapshotLoading(true);
    try {
      const response = await founderDashboardAPI.getOnboardingTodaySnapshot();
      setTodaySnapshot(response.data);
    } catch (err) {
      console.error('[OnboardingUsers] Error fetching today snapshot:', err);
      setTodaySnapshot(null);
    } finally {
      setSnapshotLoading(false);
    }
  };

  useEffect(() => {
    setUserData(null);
    setCohortData(null);
    setTodaySnapshot(null);
    setError(null);
    fetchUsers();
    fetchCohorts();
    fetchTodaySnapshot();
    const interval = setInterval(() => {
      fetchUsers();
      fetchCohorts();
      fetchTodaySnapshot();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentEnv]);

  const handleUserClick = (row) => {
    const userId = row.id || row.user_id;
    if (userId) {
      navigate(`/onboarding/users/${userId}`);
    }
  };

  // Extract stalled users (stuck >7 days at same stage)
  const stalledUsers = useMemo(() => {
    if (!userData?.users) return [];
    return userData.users
      .filter((u) => (u.days_at_stage || 0) > 7 && u.stage !== 'activated')
      .sort((a, b) => (b.days_at_stage || 0) - (a.days_at_stage || 0));
  }, [userData]);

  // Filter users by stage
  const filteredUsers = useMemo(() => {
    if (!userData?.users) return [];
    if (stageFilter === 'all') return userData.users;
    return userData.users.filter((u) => u.stage === stageFilter);
  }, [userData, stageFilter]);

  // Get unique stages for filter dropdown
  const uniqueStages = useMemo(() => {
    if (!userData?.users) return [];
    const stages = [...new Set(userData.users.map((u) => u.stage))];
    return stages.sort();
  }, [userData]);

  // Cohort table data
  const cohorts = useMemo(() => cohortData?.cohorts || [], [cohortData]);
  const cohortStages = useMemo(
    () => cohortData?.stages || Object.keys(USER_STAGE_CONFIG),
    [cohortData]
  );
  const cohortMaxCount = useMemo(() => {
    let max = 0;
    cohorts.forEach((c) => {
      cohortStages.forEach((s) => {
        const val = c.stages?.[s] || 0;
        if (val > max) max = val;
      });
    });
    return max;
  }, [cohorts, cohortStages]);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-800 font-medium">
          Couldn't load onboarding users
        </p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={fetchUsers}
          className="mt-4 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors min-h-[44px]"
        >
          Try Again
        </button>
      </div>
    );
  }

  // User list table columns
  const userColumns = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (val, row) => (
        <div>
          <span className="font-medium text-gray-900">
            {val || row.display_name || 'Unknown'}
          </span>
          {row.email && (
            <p className="text-xs text-gray-500 truncate max-w-[200px]">
              {row.email}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'stage',
      header: 'Stage',
      sortable: true,
      render: (val) => <StageBadge stage={val} />,
    },
    {
      key: 'signup_date',
      header: 'Signed Up',
      sortable: true,
      render: (val) => {
        if (!val) return <span className="text-gray-400">Unknown</span>;
        const d = new Date(val);
        return (
          <span className="text-gray-600 text-sm">
            {d.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: '2-digit',
              timeZone: 'America/Chicago',
            })}{' '}
            {d.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              timeZone: 'America/Chicago',
            })}
          </span>
        );
      },
    },
    {
      key: 'days_at_stage',
      header: 'Days at Stage',
      sortable: true,
      render: (val, row) => {
        const days = val || 0;
        const isStalled = days > 7 && row.stage !== 'activated';
        return (
          <span
            className={`text-sm font-medium ${
              isStalled ? 'text-amber-600' : 'text-gray-600'
            }`}
          >
            {days}d
          </span>
        );
      },
    },
    {
      key: 'last_activity',
      header: 'Last Active',
      sortable: true,
      render: (val) => (
        <span className="text-gray-500 text-sm">{formatRelativeDate(val)}</span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Refresh Indicator */}
      <div className="flex justify-end">
        <RefreshIndicator
          lastUpdated={lastUpdated}
          onRefresh={() => {
            fetchUsers();
            fetchCohorts();
            fetchTodaySnapshot();
          }}
          loading={loading || cohortLoading || snapshotLoading}
        />
      </div>

      {/* Section 1: Today Snapshot */}
      <section>
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-gray-900">
            Today Snapshot
          </h3>
        </div>
        {snapshotLoading ? (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <div
                key={item}
                className="h-32 overflow-hidden rounded-lg border border-gray-100 bg-white shadow-sm"
              >
                <div className="h-full animate-pulse bg-gray-50" />
              </div>
            ))}
          </div>
        ) : todaySnapshot?.cards?.length ? (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            {todaySnapshot.cards.map((card) => (
              <TodaySnapshotCard key={card.key} card={card} />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-gray-100 bg-white p-4 text-sm text-gray-500">
            Today snapshot is unavailable right now.
          </div>
        )}
      </section>

      {/* Section 2: Cohort Analysis (the money shot) */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <h3 className="text-lg font-semibold text-gray-900">
            Weekly Cohort Analysis
          </h3>
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden min-h-[44px]">
            <button
              type="button"
              onClick={() => setCohortMode('absolute')}
              className={`px-4 text-sm font-medium min-h-[44px] ${
                cohortMode === 'absolute'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              aria-pressed={cohortMode === 'absolute'}
              title="Show absolute counts"
            >
              #
            </button>
            <button
              type="button"
              onClick={() => setCohortMode('relative')}
              className={`px-4 text-sm font-medium min-h-[44px] border-l border-gray-300 ${
                cohortMode === 'relative'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              aria-pressed={cohortMode === 'relative'}
              title="Show percentage of cohort size"
            >
              %
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-3">
          Click any cell to drill down to those users.
        </p>
        {cohortLoading ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
            <div className="h-48 bg-gray-100 rounded" />
          </div>
        ) : cohorts.length > 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                      Cohort
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Size
                    </th>
                    {cohortStages.map((stage) => {
                      const config = USER_STAGE_CONFIG[stage];
                      return (
                        <th
                          key={stage}
                          className="px-2 pt-24 pb-3 text-center text-xs font-semibold text-gray-600 tracking-wider min-w-[40px]"
                        >
                          <div className="flex flex-col items-center gap-1">
                            <div className="whitespace-nowrap origin-bottom-left rotate-[-45deg] translate-x-2 translate-y-1">
                              {config?.label || stage}
                            </div>
                            {config?.tip && (
                              <span className="relative group">
                                <InformationCircleIcon className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
                                <span className="absolute left-5 top-1/2 -translate-y-1/2 z-50 hidden group-hover:block w-56 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg leading-relaxed text-left font-normal normal-case tracking-normal">
                                  {config.tip}
                                </span>
                              </span>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {[...cohorts].reverse().map((cohort, idx) => (
                    <tr key={cohort.week || idx}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap sticky left-0 bg-white z-10">
                        {cohort.week_label || cohort.week || `Week ${idx + 1}`}
                      </td>
                      <td className="px-3 py-3 text-center text-sm font-semibold text-gray-700">
                        {cohort.total || cohort.size || 0}
                      </td>
                      {cohortStages.map((stage) => {
                        const count = cohort.stages?.[stage] || 0;
                        const pct = cohort.total > 0 ? Math.round((count / cohort.total) * 100) : 0;
                        const display = count === 0
                          ? '-'
                          : cohortMode === 'relative'
                            ? `${pct}%`
                            : count;
                        const tooltip = count > 0
                          ? `View ${count} user${count !== 1 ? 's' : ''} (${pct}% of cohort) from ${cohort.week} at ${USER_STAGE_CONFIG[stage]?.label || stage}`
                          : undefined;
                        return (
                          <td
                            key={stage}
                            className={`px-3 py-3 text-center text-xs font-medium ${heatmapColor(count, cohortMaxCount, cohortMode, pct)} ${count > 0 ? 'cursor-pointer hover:ring-2 hover:ring-blue-400 hover:ring-inset' : ''}`}
                            onClick={() => {
                              if (count > 0) {
                                if (stage === 'personal_group_started') {
                                  navigate(`/onboarding/personal-groups`);
                                } else {
                                  navigate(`/onboarding/funnels/users/${stage}?cohort=${cohort.week}`);
                                }
                              }
                            }}
                            title={tooltip}
                          >
                            {display}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center text-gray-400">
            Cohort data will appear once there are enough signups to analyze.
          </div>
        )}
      </div>

      {/* Section 3: Stalled User Alerts */}
      {stalledUsers.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600" />
            <h3 className="text-lg font-semibold text-amber-900">
              Stalled Users ({stalledUsers.length})
            </h3>
          </div>
          <p className="text-amber-700 text-sm mb-4">
            These people have been stuck at the same stage for more than 7 days.
          </p>
          <div className="space-y-2">
            {stalledUsers.slice(0, 10).map((user) => (
              <div
                key={user.id || user.user_id}
                className="flex items-center justify-between bg-white rounded-lg px-4 py-3 cursor-pointer hover:bg-amber-50 transition-colors min-h-[44px]"
                onClick={() => handleUserClick(user)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleUserClick(user);
                  }
                }}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-medium text-gray-900 truncate">
                    {user.name || user.display_name || 'Unknown'}
                  </span>
                  <StageBadge stage={user.stage} />
                </div>
                <span className="text-amber-700 font-semibold text-sm flex-shrink-0 ml-2">
                  {user.days_at_stage}d stuck
                </span>
              </div>
            ))}
            {stalledUsers.length > 10 && (
              <p className="text-amber-600 text-sm text-center pt-1">
                + {stalledUsers.length - 10} more stalled users
              </p>
            )}
          </div>
        </div>
      )}

      {/* Section 4: User List */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <UserGroupIcon className="w-5 h-5 text-gray-500" />
            All Users
            {filteredUsers.length > 0 && (
              <span className="text-sm font-normal text-gray-500">
                ({filteredUsers.length})
              </span>
            )}
          </h3>
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-4 h-4 text-gray-400" />
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
            >
              <option value="all">All Stages</option>
              {uniqueStages.map((stage) => (
                <option key={stage} value={stage}>
                  {USER_STAGE_CONFIG[stage]?.label || stage}
                </option>
              ))}
            </select>
          </div>
        </div>
        <DataTable
          data={filteredUsers}
          columns={userColumns}
          onRowClick={handleUserClick}
          loading={loading}
          emptyMessage="No users found. Users will appear here as they sign up."
          defaultSortKey="signup_date"
          defaultSortOrder="desc"
        />
      </div>

    </div>
  );
}
